import React, { useEffect, useState } from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from "recharts";
import { api } from "@/lib/api";

const card = "bg-white border border-neutral-200 p-5";
const tid = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

function Stat({ label, value, note }: { label: string; value: React.ReactNode; note?: string }) {
  return (
    <div className={card} data-testid={`blog-stat-${tid(label)}`}>
      <p className="eyebrow text-[10px] text-neutral-500">{label}</p>
      <p className="font-display text-3xl mt-2">{value}</p>
      {note && <p className="text-[11px] text-neutral-500 mt-1">{note}</p>}
    </div>
  );
}

function Panel({ title, testid, children }: { title: string; testid: string; children: React.ReactNode }) {
  return <div className={card} data-testid={testid}><p className="eyebrow text-[10px] text-neutral-500 mb-3">{title}</p>{children}</div>;
}

function Rows({ rows, empty }: { rows: [string, React.ReactNode][]; empty: string }) {
  if (!rows.length) return <p className="text-sm text-neutral-500">{empty}</p>;
  return <ul className="space-y-1.5 text-sm">{rows.map(([k, v], i) => <li key={i} className="flex justify-between gap-3"><span className="truncate">{k}</span><span className="text-neutral-600 shrink-0">{v}</span></li>)}</ul>;
}

function DailyChart({ daily }: { daily: any[] }) {
  const data = daily.map((d) => ({ ...d, label: new Date(d.day + "T00:00").toLocaleDateString("en-AU", { day: "numeric", month: "short" }) }));
  return (
    <Panel title="Daily views · last 30 days" testid="blog-daily-chart">
      <div className="h-40">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
            <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={4} tickLine={false} axisLine={false} />
            <YAxis allowDecimals={false} tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
            <Tooltip cursor={{ fill: "rgba(0,0,0,0.04)" }} contentStyle={{ fontSize: 12 }} />
            <Bar dataKey="views" fill="var(--c-primary, #1a1a1a)" radius={[2, 2, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Panel>
  );
}

const secs = (s: number) => (s >= 60 ? `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, "0")}s` : `${s}s`);

function ReadingTable({ rows }: { rows: any[] }) {
  if (!rows.length) return <p className="text-sm text-neutral-500">No reading data yet — it builds up as readers open articles.</p>;
  return (
    <table className="w-full text-sm" data-testid="blog-reading-table">
      <thead><tr className="text-[10px] uppercase tracking-wider text-neutral-500 text-left"><th className="pb-2 font-normal">Category</th><th className="pb-2 font-normal text-right">Avg time</th><th className="pb-2 font-normal text-right">Scroll</th><th className="pb-2 font-normal text-right">Finish</th><th className="pb-2 font-normal text-right">Views</th></tr></thead>
      <tbody>{rows.map((r) => (
        <tr key={r.name} className="border-t border-neutral-100" data-testid={`blog-reading-${tid(r.name)}`}>
          <td className="py-1.5">{r.name}</td>
          <td className="py-1.5 text-right">{secs(r.avg_seconds)}</td><td className="py-1.5 text-right">{r.avg_depth}%</td><td className="py-1.5 text-right">{r.read_through}%</td><td className="py-1.5 text-right">{r.views}</td>
        </tr>
      ))}</tbody>
    </table>
  );
}

export function BlogDashboard() {
  const [d, setD] = useState<any>(null);
  useEffect(() => { api.get("/admin-x/blog/dashboard").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return null;
  const a = d.articles, v = d.views;
  return (
    <section className="mb-8 space-y-4" data-testid="blog-dashboard">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Articles" value={a.total} note={`${a.live} live · ${a.scheduled} scheduled · ${a.hidden} hidden`} />
        <Stat label="Views this week" value={v.week} note="last 7 days" />
        <Stat label="Views this month" value={v.month} note="last 30 days" />
        <Stat label="Views 90 days" value={v.days90} note={`${v.all_time} all time`} />
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <DailyChart daily={d.daily} />
        <Panel title="Top 5 most viewed (all time)" testid="blog-top-viewed">
          <Rows rows={d.top.filter((t: any) => t.views).map((t: any) => [t.title, `${t.views} view${t.views === 1 ? "" : "s"} · ${t.views_30} in 30d`])} empty="No views counted yet — numbers build up from today." />
        </Panel>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <Panel title="Where readers spend their time · last 90 days" testid="blog-reading-insight">
          <ReadingTable rows={d.reading} />
        </Panel>
        <Panel title="Articles per category" testid="blog-per-category">
          <Rows rows={d.categories.map((c: any) => [c.name, c.count])} empty="No categories yet." />
        </Panel>
        <Panel title={`Likes & shares · ${d.likes} likes · ${d.shares.total} shares`} testid="blog-likes-shares">
          <Rows rows={d.shares.by_network.map((n: any) => [n.label, n.count])} empty="No shares yet." />
        </Panel>
        <Panel title={`Needs attention · ${d.attention.count} live with no views in 90 days`} testid="blog-attention">
          <Rows rows={d.attention.items.map((p: any) => [p.title, p.date || ""])} empty="Every live article has been read recently." />
        </Panel>
      </div>
    </section>
  );
}
